import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, CircularProgress, Alert,
  Snackbar, Fade, Button, Stack, Grid, useMediaQuery, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, LinearProgress, Divider, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Refresh, Analytics, Download, NavigateBefore, NavigateNext,
  HomeWork, Payments, CheckCircle, Visibility, Clear, ExpandMore,
  ShoppingCart, Person, AttachMoney, Receipt,
} from '@mui/icons-material';
import { getRekapPerBantuan, getDetailAnggaranBantuan } from '../API/transaksiBantuan';
import { openInAppFilePreview } from '../../utils/filePreviewBus';

const STATUS_PROSES = ['Usulan Diterima', 'Dalam Proses', 'Selesai'];
const JENIS_BANTUAN = [
  'Rehab Rumah Tidak Layak Huni',
  'Pembangunan Rumah Layak Huni',
  'Rumah Layak Huni',
  'Rumah Tidak Layak Huni',
];

const formatRp = (n) => (!n && n !== 0) ? 'Rp 0' : 'Rp ' + Number(n).toLocaleString('id-ID');
const formatDate = (d) => d
  ? new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
  : '-';

const RekapCard = ({ icon, label, value, color }) => (
  <Card elevation={2} sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ bgcolor: `${color}.light`, color: `${color}.main`, borderRadius: 2, p: 1.2 }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: 16, sm: 20 } }}>{value}</Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const BASE = process.env.REACT_APP_URL || '';
const fileUrl = (name) => {
  if (!name) return '';
  if (String(name).startsWith('http')) return name;
  if (String(name).startsWith('kwitansi_')) return `${BASE}/files/kwitansi-upah/${name}`;
  return `${BASE}/files/images/${name}`;
};

/** Modal detail: anggaran + pembelian + upah tukang */
const DetailRekapModal = ({ open, onClose, BantuanId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !BantuanId) return;
    setLoading(true);
    setError(null);
    getDetailAnggaranBantuan(BantuanId)
      .then((res) => setData(res.data?.data || null))
      .catch((e) => {
        console.error(e);
        setError(e.response?.data?.message || 'Gagal memuat detail rekap');
      })
      .finally(() => setLoading(false));
  }, [open, BantuanId]);

  const ang = data?.anggaran || {};
  const pembelian = data?.pembelian || [];
  const upahList = data?.upahTukang || [];
  const supplier = data?.supplier || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Detail Rekap Anggaran</Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
            <Clear fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !data ? (
          <Alert severity="info">Tidak ada data</Alert>
        ) : (
          <Box sx={{ py: 1 }}>
            {/* Info penerima */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
                  <Typography variant="caption" color="text.secondary">Penerima Bantuan</Typography>
                  <Typography variant="h6">#{data.bantuan?.id} — {data.pemohon?.nama || data.bantuan?.namaLengkap || '-'}</Typography>
                  <Typography variant="body2">{data.bantuan?.JenisBantuan || '-'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[data.pemohon?.desaKelurahan, data.pemohon?.kecamatan].filter(Boolean).join(', ') || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    TFL: {data.bantuan?.namaTFL || '-'} · AGR: {data.bantuan?.namaAgr || '-'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
                  <Typography variant="caption" color="text.secondary">Pemakaian Anggaran</Typography>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                    <Typography variant="body2">Terpakai</Typography>
                    <Typography variant="body2" fontWeight={700}>{ang.persentaseTerpakai || 0}%</Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(Number(ang.persentaseTerpakai) || 0, 100)}
                    color={(ang.persentaseTerpakai || 0) >= 80 ? 'error' : (ang.persentaseTerpakai || 0) >= 50 ? 'warning' : 'success'}
                    sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Pengeluaran {formatRp(ang.totalPengeluaran)} dari {formatRp(ang.totalAnggaran)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Kartu nominal */}
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              <AttachMoney sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
              Ringkasan Anggaran
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {[
                { label: 'Total Anggaran', value: ang.totalAnggaran, color: '#e3f2fd', tc: 'primary.main' },
                { label: 'Nominal Material', value: ang.nominalMaterial ?? data.bantuan?.nominalMaterial, color: '#e0f2fe', tc: 'info.main' },
                { label: 'Nominal Upah Kerja', value: ang.nominalUpahKerja ?? data.bantuan?.nominalUpahKerja, color: '#f3e8ff', tc: '#7c3aed' },
                { label: 'Total Pembelian', value: ang.totalPembelian, color: '#fff3e0', tc: 'warning.main' },
                { label: 'Total Upah Tukang', value: ang.totalUpahTukang, color: '#fef3c7', tc: '#b45309' },
                { label: 'Sisa Anggaran', value: ang.sisaAnggaran, color: '#e8f5e9', tc: 'success.main' },
              ].map((c) => (
                <Grid item xs={6} sm={4} md={2} key={c.label}>
                  <Card sx={{ bgcolor: c.color, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: c.tc, fontSize: 13 }}>
                        {formatRp(c.value || 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pembelian */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2" fontWeight={700}>
                  <ShoppingCart sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                  Data Pembelian ({pembelian.length})
                </Typography>
                <Chip size="small" sx={{ ml: 1 }} label={formatRp(ang.totalPembelian || 0)} color="warning" variant="outlined" />
              </AccordionSummary>
              <AccordionDetails>
                {pembelian.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Belum ada data pembelian.</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {pembelian.map((p) => (
                      <Paper key={p.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              Transaksi #{p.id} · {p.supplier?.nama || p.supplier?.namaSupplier || 'Supplier'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(p.tanggal || p.createdAt)}
                              {p.statusPengiriman ? ` · ${p.statusPengiriman}` : ''}
                              {p.statusPembayaran ? ` · ${p.statusPembayaran}` : ''}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={700} color="warning.main">
                            {formatRp(p.totalTransaksi)}
                          </Typography>
                        </Stack>
                        {(p.items || p.DetailTransaksi || []).length > 0 && (
                          <>
                            {/* Tampilkan kategori pembelian jika ada */}
                            {Array.isArray(p.items) && p.items.length > 0 && p.items[0].kategoriPembelian && (
                              <Chip 
                                size="small" 
                                label={p.items[0].kategoriPembelian} 
                                variant="outlined" 
                                color="primary" 
                                sx={{ mb: 1, height: 24 }} 
                              />
                            )}
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Barang</TableCell>
                                  <TableCell align="right">Harga</TableCell>
                                  <TableCell align="right">Qty</TableCell>
                                  <TableCell align="right">Subtotal</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(p.items || p.DetailTransaksi || []).map((it) => (
                                  <TableRow key={it.id}>
                                    <TableCell>{it.namaBarang}</TableCell>
                                    <TableCell align="right">{formatRp(it.hargaSatuan)}</TableCell>
                                    <TableCell align="right">{it.jumlah || it.jumlahBarang} {it.satuan || ''}</TableCell>
                                    <TableCell align="right">{formatRp(it.total || it.totalTransaksi)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                )}

                {supplier.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">Ringkasan per Supplier</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {supplier.map((s) => (
                        <Chip
                          key={s.id}
                          size="small"
                          variant="outlined"
                          label={`${s.nama}: ${formatRp(s.totalTransaksi)} (${s.jumlahPembelian}x)`}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Upah tukang */}
            <Accordion defaultExpanded sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2" fontWeight={700}>
                  <Person sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                  Upah Tukang ({upahList.length})
                </Typography>
                <Chip size="small" sx={{ ml: 1 }} label={formatRp(ang.totalUpahTukang || 0)} color="warning" variant="outlined" />
              </AccordionSummary>
              <AccordionDetails>
                {upahList.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Belum ada data upah tukang.</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {upahList.map((u) => (
                      <Paper key={u.id} variant="outlined" sx={{ p: 1.5, bgcolor: '#fffbeb' }}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Tahap</Typography>
                            <Box>
                              <Chip
                                size="small"
                                label={u.tahap === 'tahap_1' ? 'Tahap I (30-50%)' : u.tahap === 'tahap_2' ? 'Tahap II (100%)' : (u.tahap || '-')}
                                color={u.tahap === 'tahap_1' ? 'warning' : 'success'}
                                variant="outlined"
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Nominal</Typography>
                            <Typography variant="body2" fontWeight={700} color="primary.main">
                              {formatRp(u.totalUpah)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Tukang × Hari</Typography>
                            <Typography variant="body2">{u.totalTukang || 0} org × {u.totalHari || 0} hari</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Upah Harian</Typography>
                            <Typography variant="body2">{formatRp(u.upahHarian || 0)}</Typography>
                          </Grid>
                        </Grid>
                        {u.keterangan && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Catatan: {u.keterangan}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Tanggal: {formatDate(u.tanggal || u.createdAt)}
                          {u.admin?.username ? ` · oleh ${u.admin.username}` : ''}
                        </Typography>
                        {(u.fotoKwitansi || []).length > 0 && (
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            {u.fotoKwitansi.map((f) => {
                              const name = f.foto_kwitansi || f;
                              const url = fileUrl(name);
                              return (
                                <Box
                                  key={f.id || name}
                                  component="img"
                                  src={url}
                                  alt="kwitansi"
                                  onClick={() => openInAppFilePreview({ url, title: 'Foto Kwitansi Upah' })}
                                  sx={{
                                    width: 72, height: 72, objectFit: 'cover', borderRadius: 1,
                                    border: '1px solid #e0e0e0', cursor: 'pointer',
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>

            {/* RAB singkat - dihapus */}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Tutup</Button>
      </DialogActions>
    </Dialog>
  );
};

const RekapBantuan = () => {
  const [rows, setRows] = useState([]);
  const [ringkasan, setRingkasan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchName, setSearchName] = useState('');
  const [kecamatan, setKecamatan] = useState('');
  const [statusProses, setStatusProses] = useState('');
  const [jenisBantuan, setJenisBantuan] = useState('');
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const isMobile = useMediaQuery('(max-width:800px)');
  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRekapPerBantuan({
        tahun: tahun || undefined,
        page,
        limit,
      });
      let list = res.data?.data || [];

      // Filter client-side untuk nama/kecamatan/status/jenis (API rekap fokusutan)
      if (searchName.trim()) {
        const q = searchName.trim().toLowerCase();
        list = list.filter((r) =>
          String(r.pemohon?.nama || '').toLowerCase().includes(q)
          || String(r.id || '').includes(q)
        );
      }
      if (kecamatan.trim()) {
        const q = kecamatan.trim().toLowerCase();
        list = list.filter((r) => String(r.pemohon?.kecamatan || '').toLowerCase().includes(q));
      }
      if (jenisBantuan) {
        list = list.filter((r) => r.JenisBantuan === jenisBantuan);
      }
      // statusProses tidak selalu ada di rekap per-bantuan; skip jika tidak tersedia

      setRows(list);
      setRingkasan(res.data?.ringkasan || null);
      setTotalItems(res.data?.totalItems ?? list.length);
      setTotalPages(res.data?.totalPages ?? 1);
    } catch (err) {
      console.error(err);
      notify('Gagal mengambil rekap bantuan', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchName, kecamatan, statusProses, jenisBantuan, tahun]);

  useEffect(() => {
    const t = setTimeout(() => fetchData(), 400);
    return () => clearTimeout(t);
  }, [fetchData]);

  const handleReset = () => {
    setSearchName('');
    setKecamatan('');
    setStatusProses('');
    setJenisBantuan('');
    setTahun(new Date().getFullYear());
    setPage(1);
  };

  const openDetail = (id) => {
    setSelectedId(id);
    setDetailOpen(true);
  };

  const handleExportCSV = () => {
    if (rows.length === 0) return notify('Tidak ada data untuk diekspor', 'warning');
    const header = [
      'No', 'ID', 'Nama Penerima', 'Kecamatan', 'Desa', 'Jenis Bantuan',
      'Total Anggaran', 'Pembelian', 'Upah Tukang', 'Sisa', 'Terpakai %', 'Tanggal',
    ];
    const lines = rows.map((r, i) => [
      i + 1,
      r.id,
      `"${r.pemohon?.nama || ''}"`,
      r.pemohon?.kecamatan || '',
      r.pemohon?.desaKelurahan || '',
      r.JenisBantuan || '',
      r.nominal || r.anggaran?.totalAnggaran || 0,
      r.anggaran?.totalPembelian || 0,
      r.anggaran?.totalUpahTukang || 0,
      r.anggaran?.sisaAnggaran || 0,
      r.anggaran?.persentaseTerpakai || 0,
      r.tanggalBantuan || '',
    ].join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rekap_bantuan_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    notify('Rekap diekspor ke CSV');
  };

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  return (
    <div className="table-responsive rounded border">
      <div className="main-content" style={contentStyle}>
        <Box sx={{ mt: 1, mb: 2, width: '100%', maxWidth: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Analytics color="primary" />
              <Typography variant="h5" fontWeight={700}>Rekap Bantuan</Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Download />} size="small" onClick={handleExportCSV}>Export CSV</Button>
              <Button variant="outlined" startIcon={<Refresh />} size="small" onClick={fetchData}>Muat Ulang</Button>
            </Stack>
          </Box>

          {/* Stat Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <RekapCard icon={<HomeWork />} label="Total Bantuan" value={ringkasan?.totalBantuan ?? totalItems} color="primary" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RekapCard icon={<AttachMoney />} label="Total Anggaran" value={formatRp(ringkasan?.totalAnggaran || 0)} color="info" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RekapCard icon={<ShoppingCart />} label="Total Pembelian" value={formatRp(ringkasan?.totalPembelian || 0)} color="warning" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RekapCard icon={<Person />} label="Total Upah Tukang" value={formatRp(ringkasan?.totalUpahTukang || 0)} color="secondary" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RekapCard icon={<CheckCircle />} label="Sisa Anggaran" value={formatRp(ringkasan?.totalSisa || 0)} color="success" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RekapCard icon={<Payments />} label="Total Pengeluaran" value={formatRp(ringkasan?.totalPengeluaran || 0)} color="error" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RekapCard icon={<Analytics />} label="Rata-rata Terpakai" value={`${ringkasan?.rataRataPemakaian || 0}%`} color="warning" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RekapCard icon={<Analytics />} label="Halaman" value={`${page}/${totalPages}`} color="primary" />
            </Grid>
          </Grid>

          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box component="input" placeholder="Cari nama penerima..."
                  value={searchName} onChange={(e) => { setSearchName(e.target.value); setPage(1); }}
                  sx={{ px: 1.5, py: 1, border: '1px solid #ccc', borderRadius: 1, minWidth: 200, fontSize: 14 }} />
                <Box component="input" placeholder="Kecamatan"
                  value={kecamatan} onChange={(e) => { setKecamatan(e.target.value); setPage(1); }}
                  sx={{ px: 1.5, py: 1, border: '1px solid #ccc', borderRadius: 1, minWidth: 150, fontSize: 14 }} />
                <Box component="select" value={jenisBantuan}
                  onChange={(e) => { setJenisBantuan(e.target.value); setPage(1); }}
                  sx={{ px: 1.5, py: 1, border: '1px solid #ccc', borderRadius: 1, minWidth: 170, fontSize: 14 }}>
                  <option value="">Semua Jenis</option>
                  {JENIS_BANTUAN.map((j) => <option key={j} value={j}>{j}</option>)}
                </Box>
                <Box component="select" value={tahun}
                  onChange={(e) => { setTahun(Number(e.target.value)); setPage(1); }}
                  sx={{ px: 1.5, py: 1, border: '1px solid #ccc', borderRadius: 1, minWidth: 100, fontSize: 14 }}>
                  {[0, new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map((y) => (
                    <option key={y} value={y}>{y === 0 ? 'Semua Tahun' : y}</option>
                  ))}
                </Box>
                <Box component="select" value={statusProses}
                  onChange={(e) => { setStatusProses(e.target.value); setPage(1); }}
                  sx={{ px: 1.5, py: 1, border: '1px solid #ccc', borderRadius: 1, minWidth: 160, fontSize: 14 }}>
                  <option value="">Semua Status</option>
                  {STATUS_PROSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Box>
                <Button size="small" variant="outlined" onClick={handleReset}>Reset</Button>
              </Stack>

              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 900 }}>
                  <thead style={{ backgroundColor: '#f5f7fa' }}>
                    <tr>
                      {[
                        'No.', 'Penerima', 'Kecamatan', 'Jenis',
                        'Anggaran', 'Pembelian', 'Upah Tukang', 'Sisa', 'Terpakai', 'Aksi',
                      ].map((h) => (
                        <th key={h} style={{ textAlign: h === 'Aksi' || h === 'Terpakai' ? 'center' : (['Anggaran', 'Pembelian', 'Upah Tukang', 'Sisa'].includes(h) ? 'right' : 'left'), padding: '10px', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={10} style={{ padding: '32px', textAlign: 'center' }}><CircularProgress size={26} /></td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan={10} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Tidak ada data.</td></tr>
                    ) : rows.map((row, i) => {
                      const ang = row.anggaran || {};
                      return (
                        <tr key={row.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '10px' }}>{(page - 1) * limit + i + 1}</td>
                          <td style={{ padding: '10px' }}>
                            <Typography variant="body2" fontWeight={600}>{row.pemohon?.nama || '-'}</Typography>
                            <Typography variant="caption" color="text.secondary">#{row.id} · {row.pemohon?.desaKelurahan || '-'}</Typography>
                          </td>
                          <td style={{ padding: '10px' }}>{row.pemohon?.kecamatan || '-'}</td>
                          <td style={{ padding: '10px' }}>
                            <Typography variant="body2">{row.JenisBantuan || '-'}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDate(row.tanggalBantuan)}</Typography>
                          </td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: 600 }}>{formatRp(row.nominal ?? ang.totalAnggaran)}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>
                            <Typography variant="body2" color="warning.main">{formatRp(ang.totalPembelian || 0)}</Typography>
                            <Typography variant="caption" color="text.secondary">{ang.jumlahPembelian || 0} trx · {ang.jumlahItem || 0} item</Typography>
                          </td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#b45309' }}>{formatRp(ang.totalUpahTukang || 0)}</Typography>
                            <Typography variant="caption" color="text.secondary">{ang.jumlahUpahTukang || row.upahTukang?.length || 0} data</Typography>
                          </td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: 600, color: '#2e7d32' }}>{formatRp(ang.sisaAnggaran || 0)}</td>
                          <td style={{ padding: '10px', textAlign: 'center', minWidth: 90 }}>
                            <Typography variant="caption">{ang.persentaseTerpakai || 0}%</Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(Number(ang.persentaseTerpakai) || 0, 100)}
                              color={(ang.persentaseTerpakai || 0) >= 80 ? 'error' : (ang.persentaseTerpakai || 0) >= 50 ? 'warning' : 'success'}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>
                            <IconButton size="small" color="primary" onClick={() => openDetail(row.id)} title="Detail rekap">
                              <Visibility fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box component="select" value={limit}
                    onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    sx={{ px: 1, py: 0.8, border: '1px solid #ccc', borderRadius: 1, fontSize: 14 }}>
                    {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">Hal. {page} / {totalPages} ({totalItems} data)</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" size="small" startIcon={<NavigateBefore />} disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}>{!isMobile && 'Prev'}</Button>
                  <Button variant="outlined" size="small" endIcon={<NavigateNext />} disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>{!isMobile && 'Next'}</Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>

          <DetailRekapModal
            open={detailOpen}
            onClose={() => { setDetailOpen(false); setSelectedId(null); }}
            BantuanId={selectedId}
          />

          <Snackbar open={snackbar.open} autoHideDuration={4000}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            TransitionComponent={Fade} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
              severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </div>
    </div>
  );
};

export default RekapBantuan;
